import React, { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import styles from './styles/ViewScenarioTitlePage.module.css';
import { useScenario } from '../../../context/ScenarioContext';
import { transformIframes } from '../../../utils/utils';
import ScenarioComments from './ScenarioComments';
import socket from '../../../socket';

const ViewScenarioTitlePage = ({
    userId,
    incompleteLogs,
    scenario,
    setCurrentSituationId,
    preview,
    setUserKeys,
    assignmentId,
    userInfo,
}) => {
    const { startLog, setLogId } = useScenario();
    const [commentsVisible, setCommentsVisible] = useState(false);
    const [isStarting, setIsStarting] = useState(false);

    

    const handleStart = useCallback(async () => {
        if (isStarting || !scenario?._id) return;
        setIsStarting(true);

        try {
            if (scenario.isLogged && !preview) {
                let existingLog = null;
                if (incompleteLogs) {
                    for (const [logIdEntry, log] of Object.entries(incompleteLogs)) {
                        if (log.scenarioId === scenario._id) {
                            existingLog = logIdEntry;
                            break;
                        }
                    }
                }
                if (existingLog) {
                    setLogId(existingLog);
                } else if (userId) { 
                    await startLog(scenario._id, userId, scenario.creator);
                } else {
                    console.warn("Attempting to start log for anonymous user. This may not be supported by current startLog.");
                }
            }

            
            if (userId) {
                await axios.patch(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${userId}/removeKeys`,
                    { scenarioId: scenario._id },
                    { withCredentials: true }
                );
                const userResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL_LOCAL}/api/users/${userId}`,
                    { withCredentials: true }
                );
                setUserKeys(userResponse.data.keys || []);
            } else {
                
                const storedKeyObjectsRaw = sessionStorage.getItem('userKeys');
                const storedKeyObjects = storedKeyObjectsRaw ? JSON.parse(storedKeyObjectsRaw) : [];
                
                const updatedKeyObjects = storedKeyObjects.filter(keyObj => keyObj.scenarioId !== scenario._id);
                sessionStorage.setItem('userKeys', JSON.stringify(updatedKeyObjects));
                setUserKeys(updatedKeyObjects.map(ko => ko.keyId));
            }

            
            const { data: startSituation } = await axios.get(
                `${process.env.REACT_APP_API_URL_LOCAL}/api/situations/starting/${scenario._id}`
            );

            if (startSituation?._id) {
                setCurrentSituationId(startSituation._id);
                if (assignmentId) {
                    socket.emit('studentProgressUpdate', { studentId: userId, studentName:userInfo.username, studentEmail:userInfo.email, situationId: startSituation._id, assignmentId});
                }
                if (!preview) {
                    await axios.patch(
                        `${process.env.REACT_APP_API_URL_LOCAL}/api/scenarios/${scenario._id}/views`,
                        {}, { withCredentials: true }
                    );
                }
            } else {
                console.error('No starting situation found for scenario:', scenario._id);
            }
        } catch (error) {
            console.error('Error during scenario start process:', error);
        } finally {
            setIsStarting(false);
        }
    }, [
        isStarting,
        scenario,
        preview,
        incompleteLogs,
        userId,
        setCurrentSituationId,
        startLog,
        setLogId,
        setUserKeys
    ]);

    const toggleComments = useCallback(() => {
        setCommentsVisible(prev => !prev);
    }, []);

    
    const commentSection = useMemo(() => (
        <div className={styles.comments}>
            {commentsVisible ? (
                <ScenarioComments scenarioId={scenario._id} userId={userId} />
            ) : (
                <span className={styles.viewCommentsLink} onClick={toggleComments}>
                    View Comments
                </span>
            )}
        </div>
    ), [commentsVisible, scenario?._id, userId, toggleComments]);

    return (
        <div className={styles.container}>
            {scenario.image && (
                <div className={styles.imageContainer}>
                    <img src={scenario.image} alt={scenario.title || 'Scenario Image'} className={styles.image} />
                </div>
            )}

            <div className={styles.title}>
                {scenario.title}
            </div>

            {scenario.description && (
                <div className={styles.description}>
                    <div dangerouslySetInnerHTML={{ __html: transformIframes(scenario.description) }} />
                </div>
            )}

            <button
                onClick={handleStart}
                className={styles.button}
                disabled={isStarting} 
            >
                {isStarting ? 'Starting...' : 'Start'}
            </button>

            {commentSection}
        </div>
    );
};

export default React.memo(ViewScenarioTitlePage);